import { useState } from "react";
import { Link } from "react-router-dom";
import {
  cubeLogo,
  serviceCategories,
  contactInfo,
  socialLinks,
  aboutData,
  consultationLink,
} from "../data/mock";
import { Button } from "./ui/button";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const currentYear = new Date().getFullYear();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    toast.success("Thanks for subscribing! We'll be in touch.");
    setEmail("");
  };

  return (
    <footer className="relative border-t border-white/[0.06]">
      {/* About Section in Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand & About */}
          <div className="lg:col-span-4">
            <Link to="/">
              <img src={cubeLogo} alt="Cube Multimedia" className="h-20 mb-6" />
            </Link>
            <p className="text-[#71717a] text-sm leading-relaxed mb-4">
              {aboutData.tagline}
            </p>
            <p className="text-[#52525b] text-xs leading-relaxed mb-6">
              {aboutData.description}
            </p>
            <div className="flex gap-3">
              {[
                { href: socialLinks.facebook, label: "FB" },
                { href: socialLinks.instagram, label: "IG" },
                { href: socialLinks.youtube, label: "YT" },
                { href: socialLinks.linkedin, label: "LI" },
              ].map(({ href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-[#71717a] hover:text-[#dc2626] hover:border-[#dc2626]/30 transition-all duration-300 text-xs font-bold"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">
              Services
            </h4>
            <ul className="space-y-2.5">
              {serviceCategories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/services/${cat.id}`}
                    className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                  >
                    {cat.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/gmf"
                  className="text-[#dc2626] text-sm font-medium hover:text-[#ef4444] transition-colors duration-300 flex items-center gap-1"
                >
                  Book Consultation
                  <ArrowUpRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">
              Company
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/about"
                  className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  className="text-[#71717a] text-sm hover:text-white transition-colors duration-300"
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">
              Contact Us
            </h4>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 text-[#71717a] text-sm">
                <MapPin className="w-4 h-4 text-[#dc2626] mt-0.5 shrink-0" />
                <span>{contactInfo.office}</span>
              </div>
              <div className="flex items-center gap-3 text-[#71717a] text-sm">
                <Clock className="w-4 h-4 text-[#dc2626] shrink-0" />
                <span>{contactInfo.hours}</span>
              </div>
              <a
                href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[#71717a] text-sm hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-[#dc2626] shrink-0" />
                {contactInfo.whatsapp}
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-3 text-[#71717a] text-sm hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-[#dc2626] shrink-0" />
                {contactInfo.email}
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">
              Stay Updated
            </h4>
            <p className="text-[#52525b] text-xs mb-4">
              Get the latest marketing insights delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#dc2626]/40 transition-colors duration-300"
              />
              <Button
                type="submit"
                className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-2.5 rounded-lg text-sm transition-colors duration-300 flex items-center justify-center gap-2"
              >
                Subscribe
                <Send className="w-3.5 h-3.5" />
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#52525b] text-sm">
            &copy; {currentYear} Cube Multimedia. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="text-[#52525b] text-sm hover:text-[#a1a1aa] transition-colors duration-300"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-[#52525b] text-sm hover:text-[#a1a1aa] transition-colors duration-300"
            >
              Terms of Service
            </Link>
            <Link
              to="/refund-policy"
              className="text-[#52525b] text-sm hover:text-[#a1a1aa] transition-colors duration-300"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
